/**
 * Settings presentational UI (WP31) — research kill switch.
 */

export type SettingsViewState = "loading" | "error" | "ready";

export type SettingsViewProps = {
  state: SettingsViewState;
  errorMessage?: string | null;
  actionError?: string | null;
  actionMessage?: string | null;
  killSwitchEnabled: boolean;
  isAdmin: boolean;
  busy?: boolean;
  confirmOpen: boolean;
  pendingEnabled: boolean | null;
  onToggleRequest: (next: boolean) => void;
  onConfirm: () => void;
  onCancelConfirm: () => void;
  onRetry?: () => void;
};

export function SettingsView({
  state,
  errorMessage,
  actionError,
  actionMessage,
  killSwitchEnabled,
  isAdmin,
  busy,
  confirmOpen,
  pendingEnabled,
  onToggleRequest,
  onConfirm,
  onCancelConfirm,
  onRetry,
}: SettingsViewProps) {
  if (state === "loading") {
    return (
      <p data-testid="settings-loading" className="text-sm text-gray-500">
        Lade Settings…
      </p>
    );
  }

  if (state === "error") {
    return (
      <div
        data-testid="settings-error"
        className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-800"
      >
        <p>{errorMessage ?? "Settings konnten nicht geladen werden."}</p>
        {onRetry ? (
          <button
            type="button"
            data-testid="settings-retry"
            className="mt-2 text-sm font-medium underline"
            onClick={onRetry}
          >
            Erneut versuchen
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div data-testid="settings-view" className="mx-auto max-w-2xl space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-800">
          Research Kill Switch
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          Stoppt neue Research Jobs und Workflow-Bootstrap. Kein E-Mail-Send
          (Phase 1).
        </p>
      </div>

      {!isAdmin ? (
        <div
          data-testid="settings-readonly-banner"
          className="rounded-lg border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-900"
        >
          Read-only: nur Admins können den Kill Switch ändern.
        </div>
      ) : null}

      {actionMessage ? (
        <p
          data-testid="settings-action-message"
          className="rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2 text-sm text-emerald-800"
        >
          {actionMessage}
        </p>
      ) : null}
      {actionError ? (
        <p
          data-testid="settings-action-error"
          className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-sm text-red-800"
        >
          {actionError}
        </p>
      ) : null}

      <div className="rounded-lg border border-gray-200 bg-white px-4 py-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-gray-800">Status</p>
            <p
              data-testid="settings-kill-switch-status"
              className="mt-1 text-sm text-gray-600"
            >
              {killSwitchEnabled ? "Aktiv (Research gestoppt)" : "Aus (normal)"}
            </p>
          </div>
          {isAdmin ? (
            <button
              type="button"
              data-testid="settings-kill-switch-toggle"
              disabled={busy}
              onClick={() => onToggleRequest(!killSwitchEnabled)}
              className={`rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-40 ${
                killSwitchEnabled
                  ? "bg-gray-800"
                  : "bg-red-600"
              }`}
            >
              {killSwitchEnabled ? "Deaktivieren" : "Aktivieren"}
            </button>
          ) : null}
        </div>
      </div>

      {confirmOpen && pendingEnabled != null ? (
        <div
          data-testid="settings-confirm"
          className="rounded-lg border border-violet-100 bg-violet-50 px-4 py-4"
        >
          <p className="text-sm text-violet-900">
            {pendingEnabled
              ? "Kill Switch wirklich aktivieren? Neue Research Jobs und Bootstrap werden blockiert."
              : "Kill Switch wirklich deaktivieren? Research läuft wieder normal."}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              data-testid="settings-confirm-yes"
              disabled={busy}
              onClick={onConfirm}
              className="rounded-lg bg-violet-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-40"
            >
              Bestätigen
            </button>
            <button
              type="button"
              data-testid="settings-confirm-no"
              disabled={busy}
              onClick={onCancelConfirm}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 disabled:opacity-40"
            >
              Abbrechen
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
